<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import { useRegisterOnboardingNav } from '../useOnboardingNav';

const emit = defineEmits(['back', 'complete']);

const store = useStore();
const { t } = useI18n();

// Pace early stages against a multi-page crawl (e.g. handelsbeurs ~30–90s).
// Each value is the earliest elapsed ms when that stage may complete while still pending.
const STAGE_COMPLETE_AT_MS = {
  open: 5000,
  find: 14000,
  read: 28000,
};
const EARLY_FINISH_BEAT_MS = 900;

const STAGE_KEYS = ['open', 'find', 'read', 'wrap'];

const statuses = ref(
  STAGE_KEYS.reduce((acc, key) => {
    acc[key] = 'pending';
    return acc;
  }, {})
);
const animationComplete = ref(false);
const advancing = ref(false);
let cancelled = false;

const domain = computed(() => store.state.onboarding.domain);
const lindexResult = computed(() => store.state.onboarding.lindexResult);
const lindexError = computed(() => store.state.onboarding.lindexError);
const isPending = computed(
  () => store.getters['onboarding/isExtractionPending']
);
const isFailed = computed(
  () => store.getters['onboarding/isExtractionFailed']
);
const spacesCount = computed(
  () => lindexResult.value?.spaces?.length || 0
);

const title = computed(() => {
  if (isFailed.value && animationComplete.value) {
    return t('ONBOARDING_WIZARD.EXTRACTION.TITLE_FAILED');
  }
  if (animationComplete.value && !isPending.value) {
    return t('ONBOARDING_WIZARD.EXTRACTION.TITLE_DONE');
  }
  return t('ONBOARDING_WIZARD.EXTRACTION.TITLE');
});

const description = computed(() => {
  if (isFailed.value && animationComplete.value) {
    return (
      lindexError.value || t('ONBOARDING_WIZARD.EXTRACTION.DESCRIPTION_FAILED')
    );
  }
  if (animationComplete.value && !isPending.value) {
    if (spacesCount.value > 0) {
      return t('ONBOARDING_WIZARD.EXTRACTION.DESCRIPTION_FOUND', {
        count: spacesCount.value,
      });
    }
    return t('ONBOARDING_WIZARD.EXTRACTION.DESCRIPTION_EMPTY');
  }
  return t('ONBOARDING_WIZARD.EXTRACTION.DESCRIPTION', {
    url: domain.value,
  });
});

const stages = computed(() =>
  STAGE_KEYS.map(key => ({
    key,
    label: t(`ONBOARDING_WIZARD.EXTRACTION.STAGES.${key.toUpperCase()}`),
  }))
);

function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

function stageIconClass(key) {
  if (statuses.value[key] === 'loading') {
    return 'bg-woot-50 dark:bg-woot-900/30';
  }
  if (statuses.value[key] === 'done' && isFailed.value && key === 'wrap') {
    return 'bg-amber-50 dark:bg-amber-900/20';
  }
  if (statuses.value[key] === 'done') {
    return 'bg-green-50 dark:bg-green-900/20';
  }
  return '';
}

async function waitUntilExtractionSettled() {
  while (!cancelled && isPending.value) {
    await sleep(400);
  }
}

async function waitForStage(key, startedAt) {
  if (key === 'wrap') {
    await waitUntilExtractionSettled();
    return;
  }

  const completeAt = STAGE_COMPLETE_AT_MS[key] || 0;
  while (!cancelled && isPending.value) {
    const elapsed = Date.now() - startedAt;
    if (elapsed >= completeAt) return;
    await sleep(250);
  }

  // Extraction finished before this stage's milestone — brief beat, don't rush.
  if (!cancelled) await sleep(EARLY_FINISH_BEAT_MS);
}

async function runStages() {
  const startedAt = Date.now();

  for (const key of STAGE_KEYS) {
    if (cancelled) return;
    statuses.value = { ...statuses.value, [key]: 'loading' };

    await waitForStage(key, startedAt);

    if (cancelled) return;
    statuses.value = { ...statuses.value, [key]: 'done' };
  }

  animationComplete.value = true;
}

function continueAfterScan() {
  if (advancing.value) return;
  advancing.value = true;
  emit('complete');
}

useRegisterOnboardingNav(
  () => {
    if (!animationComplete.value || isPending.value) {
      return {
        hidePrimary: true,
        onBack: () => emit('back'),
      };
    }

    if (isFailed.value) {
      return {
        secondary: {
          label: t('ONBOARDING_WIZARD.EXTRACTION.TRY_ANOTHER'),
          onClick: () => emit('back'),
        },
        primary: {
          label: t('ONBOARDING_WIZARD.EXTRACTION.CONTINUE_ANYWAY'),
          loading: advancing.value,
          onClick: continueAfterScan,
        },
      };
    }

    return {
      hideBack: true,
      primary: {
        label: t('ONBOARDING_WIZARD.NEXT'),
        loading: advancing.value,
        onClick: continueAfterScan,
      },
    };
  },
  { clearOnUnmount: false }
);

onMounted(() => {
  store.dispatch('onboarding/startPolling');
  runStages();
});

onBeforeUnmount(() => {
  cancelled = true;
});
</script>

<template>
  <OnboardingStepLayout
    :step-label="t('ONBOARDING_WIZARD.PHASES.1.LABEL')"
    :title="title"
    :description="description"
  >
    <ul class="w-full flex flex-col gap-3 m-0 p-0 list-none">
      <li
        v-for="stage in stages"
        :key="stage.key"
        class="flex items-center gap-4 rounded-xl border bg-white dark:bg-slate-900/40 transition-all duration-500 ease-out overflow-hidden"
        :class="
          statuses[stage.key] === 'pending'
            ? 'opacity-0 translate-x-8 max-h-0 py-0 px-4 border-transparent pointer-events-none'
            : 'opacity-100 translate-x-0 max-h-24 px-4 py-3 border-slate-100 dark:border-slate-800'
        "
      >
        <div
          class="size-9 shrink-0 rounded-full flex items-center justify-center"
          :class="stageIconClass(stage.key)"
        >
          <span
            v-if="statuses[stage.key] === 'loading'"
            class="size-4 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
          />
          <Icon
            v-else-if="statuses[stage.key] === 'done' && isFailed && stage.key === 'wrap'"
            icon="i-lucide-triangle-alert"
            class="size-5 text-amber-600 dark:text-amber-400"
          />
          <Icon
            v-else-if="statuses[stage.key] === 'done'"
            icon="i-lucide-check"
            class="size-5 text-green-600 dark:text-green-400"
          />
        </div>
        <p class="m-0 text-base font-medium text-slate-900 dark:text-white">
          {{ stage.label }}
        </p>
      </li>
    </ul>
  </OnboardingStepLayout>
</template>
